'use client';

import React, { ChangeEventHandler, FormEvent, useMemo, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { useTranslations } from 'next-intl';

import { clearSessionData, setSessionData } from 'actions/common/sessionHandler';
import TermsAndPrivacy from 'components/auth/TermsAndPrivacy';
import useParams from 'hooks/useParams';
import useTenant from 'hooks/useTenant';
import { AuthIntentType } from 'server/nextauth/typeorm/types';
import AuthProviderButton, { ProviderKey } from 'ui/AuthProviderButton';
import Button from 'ui/Button';
import Icon from 'ui/Icon';
import Input from 'ui/Input';
import Loading from 'ui/Loader';
import PageTitle from 'ui/PageTitle';
import TenantThemeButton from 'ui/TenantThemeButton';
import { PublicOAuthProvider } from 'utils/GeneratedApi';
import { oAuthLogin } from 'utils/oAuthLogin';

type FormProps = {
  callbackUrl: string;
  defaultEmail: string;
  codeUrl: string;
  signUpUrl?: string;
  checkoutStateUuid?: string;
  authError?: string;
};

const AuthSigninForm = ({
  codeUrl,
  callbackUrl,
  defaultEmail,
  signUpUrl,
  checkoutStateUuid,
  authError = '',
}: FormProps) => {
  const tApiMessageCode = useTranslations('apiMessageCode');
  const tAuth = useTranslations('auth');
  const tId = useTranslations('id.auth');
  const { params } = useParams();
  const tAuthError = useTranslations('auth.authError');

  let authErrorMessage = '';
  if (authError) {
    authErrorMessage = tAuthError(authError);
  }

  const [errorMessage, setErrorMessage] = useState<string>(authErrorMessage);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userExists, setUserExists] = useState<boolean>(true);
  const formattedEmail = defaultEmail.replace(/ /g, '+');
  const [email, setEmail] = useState<string>(formattedEmail);

  const { push } = useRouter();
  const tenant = useTenant();

  /**
   * We want to keep the email, so you can switch between signup and signin
   */
  const switchToSignupUrl = useMemo(() => {
    const url = new URL(signUpUrl || '/auth/signup', 'https://host.com'); // dummy host
    url.searchParams.set('email', email);

    // Add checkoutStateUuid to the signup URL, if it exists
    if (checkoutStateUuid) {
      url.searchParams.set('checkoutStateUuid', checkoutStateUuid);
    }

    url.searchParams.set('callbackUrl', callbackUrl);
    return url.pathname + url.search;
  }, [signUpUrl, email, checkoutStateUuid, callbackUrl]);

  const isAdminPath = params.includes('admin');

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    await clearSessionData('auth-intent');

    try {
      const apiURL = new URL('/api/user-exists-or-update', window.location.origin);
      apiURL.searchParams.set('email', email.toLowerCase());

      // Add checkoutStateUuid to the URL, if it exists
      if (checkoutStateUuid) {
        apiURL.searchParams.set('checkoutStateUuid', checkoutStateUuid);
      }

      const emailExistsResponse = await fetch(apiURL, { method: 'POST' });

      if (emailExistsResponse.status !== 200) {
        setUserExists(false);
        setIsLoading(false);
        return;
      }
      const response = await signIn('email', {
        email,
        redirect: false,
      });

      if (response?.status === 200) {
        const codeNavigatorUrl = new URL(codeUrl, window.location.origin);
        codeNavigatorUrl.searchParams.set('email', email.toLowerCase());
        codeNavigatorUrl.searchParams.set('callbackUrl', callbackUrl);

        // Add checkoutStateUuid to the URL, if it exists
        if (checkoutStateUuid) {
          codeNavigatorUrl.searchParams.set('checkoutStateUuid', checkoutStateUuid);
        }

        const emailExistsResponseJson = await emailExistsResponse.json();

        if (emailExistsResponseJson?.matchEmailDomain) {
          codeNavigatorUrl.searchParams.set(
            'matchEmailDomain',
            emailExistsResponseJson.matchEmailDomain,
          );
        }

        push(codeNavigatorUrl.pathname + codeNavigatorUrl.search);
      } else {
        setUserExists(true);
        setErrorMessage('signInError');
        setIsLoading(false);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error && error?.message === 'notFound'
          ? tApiMessageCode('memberUserNotFoundPrefix')
          : tApiMessageCode('signInError');
      setErrorMessage(errorMessage);
      setIsLoading(false);
    }
  };

  async function handleOAuthLogin(provider: PublicOAuthProvider) {
    await clearSessionData('auth-intent');
    await setSessionData('auth-intent', AuthIntentType.SIGNIN);

    const redirectUrlAfterFailure = window.location.href;

    await oAuthLogin(provider, callbackUrl, redirectUrlAfterFailure);
  }

  const handleEmailChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setEmail(event.currentTarget.value);
  };

  return (
    <>
      <div className="pb-8">
        <PageTitle title={tAuth('signIn')} />
      </div>

      <form className="w-full" onSubmit={handleLogin}>
        {errorMessage ? <p className="mt-6 text-left text-red">{errorMessage}</p> : null}
        {!userExists ? (
          <div className="flex flex-col">
            <p className="text-left text-red">{tAuth('userDoesNotExistHelpText')}</p>
            {signUpUrl ? (
              <p className="text-left">
                <Link href={switchToSignupUrl} prefetch={false} className="underline font-bold">
                  {tAuth('signUp')}
                </Link>{' '}
                {tAuth('signUpHelpText')}
              </p>
            ) : null}
          </div>
        ) : null}

        <Input
          layout="solid"
          color="light"
          type="email"
          dataTestId="email"
          id="email"
          name="email"
          label={tAuth('email')}
          placeholder={tAuth('emailPlaceHolder')}
          required
          disabled={isLoading}
          value={email}
          onChange={handleEmailChange}
          icon={() => <Icon iconName="Envelope" />}
        />
        <div className="mt-4">
          {isAdminPath ? (
            <Button
              dataTestId="signInButton"
              id="signInButton"
              disabled={isLoading}
              theme="solid"
              type="submit"
              size="wide"
            >
              {isLoading ? <Loading /> : tAuth('signIn')}
            </Button>
          ) : (
            <TenantThemeButton id="signInButton" size="wide" disabled={isLoading} type="submit">
              {isLoading ? <Loading /> : tAuth('signIn')}
            </TenantThemeButton>
          )}
        </div>
        {signUpUrl ? (
          <p className="mt-6 text-center">
            {tAuth('dontHaveAccount')}{' '}
            <Link
              href={switchToSignupUrl}
              prefetch={false}
              className="underline hover:no-underline"
            >
              {tAuth('openAccount')}
            </Link>
          </p>
        ) : null}
      </form>
      {tenant?.oauthProviders && tenant?.oauthProviders.length > 0 ? (
        <>
          <div className="flex items-center space-x-4 mt-4 mb-3">
            <div className="flex-1 h-px bg-gray-400"></div>
            <span className="text-gray-600">{tId('or')}</span>
            <div className="flex-1 h-px bg-gray-400"></div>
          </div>
          <ul className="flex flex-col items-center">
            {tenant.oauthProviders.map((provider) => (
              <li key={provider.name} className="py-1">
                <AuthProviderButton
                  provider={provider.name as ProviderKey}
                  action="login"
                  onClick={() => handleOAuthLogin(provider)}
                  buttonKey={`${provider.name}.signinButton`}
                />
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <TermsAndPrivacy />
    </>
  );
};

export default AuthSigninForm;
